import {
  getGetCampaignApiV1QueryKey,
  useGetChannelsApiV1Suspense,
  useJoinCampaignWaitlistApiV1,
} from "@/api/__generated__/api.schema";
import { CampaignCampaignStatus, CampaignUserRegisterStatus } from "@/api/__generated__/models";
import { ExternalLink } from "@/components/external-link";
import { CAMPAIGN_NOTICES } from "@/constants/campaign";
import { getAssetsUrl, routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { WorkspaceLink } from "@/lib/router/workspace-link";
import { trackPixelReservedEvent } from "@/lib/track/pixel";
import { NewTrackingEvent } from "@/lib/track/track-event";
import { trackNewEvent } from "@/lib/track/track-event";
import { cn } from "@/lib/utils";
import { MusicTrack } from "@/routes/campaigns/[id]/components/music-track";
import { TargetViewProgress } from "@/routes/campaigns/[id]/components/target-view-progress";
import { useCampaignId } from "@/routes/campaigns/hooks/use-campaign-id";
import { useCurrentCampaignInfo } from "@/routes/campaigns/hooks/use-current-campaign-info";
import { formatFullDateTimeRange } from "@/utils/date";
import { formatNumber } from "@/utils/format";
import { useToastMessage } from "@backpac/monica";
import { Button, List, ListItem, Separator, Typography, useConfirm } from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { InfoIcon } from "lucide-react";
import { type ComponentProps, useEffect, useState } from "react";
import { Temporal } from "temporal-polyfill";

// @deprecated
export function CampaignViewsPage() {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const id = useCampaignId();
  const info = useCurrentCampaignInfo();

  useEffect(() => {
    // Set visible after component mounts to trigger animation
    setIsVisible(true);
    trackNewEvent(NewTrackingEvent.ViewCampaignPage, {
      campaign_id: id,
      campaign_title: info.meta?.title ?? "",
    });
    trackPixelReservedEvent("ViewContent");
  }, [id, info.meta?.title]);

  return (
    <div
      className={cn(
        "relative flex flex-col w-full max-w-[620px] mx-auto mt-8 mb-20",
        "animate-in duration-300 ease-in-out",
        isVisible ? "fade-in-0 slide-in-from-bottom-8" : "opacity-0 translate-y-8",
      )}
    >
      <Typography size={isMobile ? "lg" : "xl"} font="unbounded" className="px-2 mb-6">
        {info.title}
      </Typography>
      <HeroSection />
      <EventDetailsSection />
      <HowToParticipateSection />
      <TrackSection />
      <ParticipationCriteriaSection />
      <div className="fixed bottom-0 left-0 right-0 w-full ml-0 md:ml-[7.5rem]">
        <div
          className="px-6 py-4 bg-white max-w-[620px] mx-auto flex items-center justify-between"
          style={{ boxShadow: "0px -2px 6px 0px rgba(15, 15, 15, 0.10)" }}
        >
          <CampaignViewCTAMessage />
          <CampaignViewCTA position="bottom" />
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  const info = useCurrentCampaignInfo();

  return (
    <div
      className="relative w-full bg-cover bg-center"
      style={{ backgroundImage: `url(${info.meta?.material?.hero})` }}
    >
      <div className={`absolute inset-0 ${info.meta?.styles.hero}`} />
      <div className="relative h-full flex flex-col items-center justify-center mb-[59px]">
        <Typography className={`${info.meta?.styles.text} mt-[10px] mb-[22px]`} size="xs" weight={500} font="spring">
          BGM Campaign
        </Typography>
        <div className="mb-2">
          <Typography className={`${info.meta?.styles.heroText} text-center`} font="fanwood">
            {info.trackName}
          </Typography>
        </div>
        <Typography className="text-white text-center mb-6" size="paragraphXs" weight={350} font="spring">
          RPM $1 guaranteed
          <br />
          Earn $1 per 1,000 Shorts US views with this track
        </Typography>
        <CampaignViewCTA size="md" className="mb-3" position="top" />
        <Typography className="opacity-80 text-white text-center" size="xs" font="spring">
          {formatFullDateTimeRange([
            Temporal.PlainDateTime.from(info.campaignUploadStartDate),
            Temporal.PlainDateTime.from(info.campaignUploadEndDate),
          ])}{" "}
          (PDT)
        </Typography>
      </div>
    </div>
  );
}

function EventDetailsSection() {
  const info = useCurrentCampaignInfo();

  return (
    <div className={`flex flex-col items-center gap-8 py-10 px-6 self-stretch w-full ${info.meta?.styles.bgLight}`}>
      <Typography className="text-center" size="md" color="white" font="spring">
        Use the provided track in your YouTube Shorts and
        <br />
        earn campaign rewards at an RPM $1
      </Typography>

      <div className="flex flex-col items-center gap-6 w-full">
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col desktop:flex-row w-full justify-between bg-[#00000040] rounded-md border-none p-4 gap-3 desktop:gap-8">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Typography className="text-[#ffffffcc]" font="spring" size="xs">
                  Track title
                </Typography>
                <Typography className="text-white" font="spring" size="xs">
                  {info.title}
                </Typography>
              </div>

              <div className="flex flex-col gap-1">
                <Typography className="text-[#ffffffcc] whitespace-nowrap" font="spring" size="xs">
                  Campaign sign-up period(PDT)
                </Typography>
                <Typography className="text-white whitespace-nowrap" font="spring" size="xs">
                  {formatFullDateTimeRange([
                    Temporal.PlainDateTime.from(info.campaignSignUpStartDate),
                    Temporal.PlainDateTime.from(info.campaignSignUpEndDate),
                  ])}
                </Typography>
              </div>

              <div className="flex flex-col gap-1">
                <Typography className="text-[#ffffffcc] whitespace-nowrap" font="spring" size="xs">
                  Upload period(PDT)
                </Typography>
                <Typography className="text-white whitespace-nowrap" font="spring" size="xs">
                  {formatFullDateTimeRange([
                    Temporal.PlainDateTime.from(info.campaignUploadStartDate),
                    Temporal.PlainDateTime.from(info.campaignUploadEndDate),
                  ])}
                </Typography>
              </div>

              <div className="flex flex-col gap-1">
                <Typography className="text-[#ffffffcc] whitespace-nowrap" font="spring" size="xs">
                  Target country
                </Typography>
                <Typography className="text-white whitespace-nowrap" font="spring" size="xs">
                  {info.meta?.target_country}
                </Typography>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Typography className="text-[#ffffffcc] whitespace-nowrap" font="spring" size="xs">
                  Target views
                </Typography>
                <Typography className="text-white whitespace-nowrap" font="spring" size="xs">
                  {formatNumber(info.targetView, 0)}
                </Typography>
              </div>

              <div className="flex flex-col gap-1">
                <Typography className="text-[#ffffffcc]" font="spring" size="xs">
                  RPM
                </Typography>
                <Typography className="text-white" font="spring" size="xs">
                  ${info.rpm} (US view only)
                </Typography>
              </div>

              <div className="flex flex-col gap-1">
                <Typography className="text-[#ffffffcc] whitespace-nowrap" font="spring" size="xs">
                  Revenue Confirmation Date
                </Typography>
                <Typography className="text-white whitespace-nowrap" font="spring" size="xs">
                  {Temporal.PlainDate.from(info.campaignRevenueConfirmationDate).toLocaleString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Typography className="text-white text-center" font="spring" size="xs">
              Convert PDT to your local time zone to ensure accurate participation
            </Typography>
            <Typography asChild className="text-white text-center underline whitespace-nowrap" font="spring" size="xs">
              <ExternalLink to="https://time.is/PDT">Check PDT time</ExternalLink>
            </Typography>
          </div>
        </div>
      </div>

      <div className="w-full px-4 desktop:px-8">
        <Separator className="opacity-20" />
      </div>
      <div className="flex flex-col items-center gap-6 w-full px-4 desktop:px-20">
        <Typography className="text-center" size="xl" color="white" font="spring">
          Target views
        </Typography>
        <TargetViewProgress />
        <div className={"w-full rounded-md bg-[#00000040]"}>
          <div className="flex flex-col items-start gap-2 p-4">
            <Typography className="text-white w-fit whitespace-nowrap" font="spring" size="xs">
              Campaign Update
            </Typography>
            <List className="self-stretch">
              {[
                "The campaign is extended to April 6.",
                "Video can earn more qualifying views unless the campaign ends early.",
                "Revenue Confirmation Date is now April 21.",
              ].map((item, index) => (
                <Typography asChild key={index} className="text-white" font="spring" size="paragraphXs">
                  <ListItem>{item}</ListItem>
                </Typography>
              ))}
            </List>
          </div>
        </div>
      </div>
    </div>
  );
}

const participationSteps = [
  {
    id: 1,
    description: "Choose an upload date within the campaign period.",
  },
  {
    id: 2,
    description: "Create a Short that is at least 35 seconds long and use the specified track as background music.",
  },
  {
    id: 3,
    description: "Upload your video on the selected date.",
  },
  {
    id: 4,
    description: "After uploading, submit your Short URL on the [Campaign detail] page to complete your participation.",
  },
];

function HowToParticipateSection() {
  return (
    <div className="flex flex-col w-full items-center gap-6 py-10 px-6 desktop:px-10 bg-white">
      <Typography className="text-[#0e0e0e] text-center" size="xl" font="spring">
        How to participate
      </Typography>

      <div className="flex flex-col items-center gap-4 w-full max-w-[540px]">
        <div className="grid grid-cols-2 gap-4 w-full">
          {participationSteps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-start gap-3">
              <img src={getAssetsUrl(`participate-step-${index + 1}.png`)} alt={step.description} />
              <Typography className="text-[#0e0e0e] w-full" font="spring" size="paragraphXs">
                {step.description}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrackSection() {
  const info = useCurrentCampaignInfo();

  return (
    <div className={`flex flex-col items-center gap-6 py-10 px-6 desktop:px-10 w-full ${info.meta?.styles.bgLight}`}>
      <Typography className="text-center" size="xl" color="white" font="spring">
        Track
      </Typography>
      <div className="flex flex-col items-start gap-4 w-full max-w-[420px]">
        <MusicTrack
          title={info.title}
          isrc={info.meta?.material.isrc ?? ""}
          coverImage={info.meta?.material?.cover ?? ""}
        />

        <div className={"w-full rounded-md bg-[#00000040]"}>
          <div className="flex flex-col items-start gap-2 p-4">
            <Typography className="text-white w-fit whitespace-nowrap" font="spring" size="xs">
              Upload Guide
            </Typography>
            <List className="self-stretch">
              {[
                "Videos must be at least 35 seconds long.",
                "Only one audio track per video is allowed.",
                "Set the audio volume to a minimum of 15%.",
              ].map((item, index) => (
                <Typography asChild key={index} className="text-white" font="spring" size="paragraphXs">
                  <ListItem>{item}</ListItem>
                </Typography>
              ))}
            </List>
          </div>
        </div>
      </div>
    </div>
  );
}

function ParticipationCriteriaSection() {
  return (
    <div className="w-full max-w-[620px] bg-[#1f1f1f] border-none rounded-none pb-4">
      <div className="flex flex-col gap-4 px-6 desktop:px-[60px] py-10">
        <div className="flex items-center gap-2">
          <div className="relative w-4 h-4">
            <InfoIcon className="w-4 h-4 text-white" />
          </div>
          <Typography className="text-white whitespace-nowrap" size="md" font="unbounded" weight={500}>
            Campaign Guideline
          </Typography>
        </div>
        {CAMPAIGN_NOTICES.map((section, index) => (
          <List key={index} className="flex flex-col items-start gap-1 w-full">
            <Typography className="w-full text-white" font="spring" size="paragraphXs">
              {section.title}
            </Typography>
            {section.content.map((paragraph, pIndex) => (
              <Typography asChild key={pIndex} className="w-full text-borderDark" font="spring" size="paragraphXs">
                <ListItem>{paragraph}</ListItem>
              </Typography>
            ))}
          </List>
        ))}
      </div>
    </div>
  );
}

function CampaignViewCTAMessage({ className }: { className?: string }) {
  const info = useCurrentCampaignInfo();

  if (info.campaignStatus === CampaignCampaignStatus.SIGN_UP) {
    if (info.userRegisterStatus === CampaignUserRegisterStatus.REGISTERED) {
      return (
        <div className={cn("space-y-[6px]", className)}>
          <Typography size="sm" weight={600}>
            Upload date
          </Typography>
          <Typography size="xs" weight={500} color="textMuted">
            {Temporal.PlainDate.from(info.estimatedUploadDate!).toLocaleString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            , 00:00 - 23:59 (PDT)
          </Typography>
        </div>
      );
    }
    return (
      <Typography size="sm" weight={600} className={className}>
        Earn $1 per 1,000 Shorts US views
      </Typography>
    );
  }

  if (info.campaignStatus === CampaignCampaignStatus.EARLY_CLOSED) {
    return (
      <Typography size="sm" weight={600} className={className}>
        No spots available
      </Typography>
    );
  }
  if (info.campaignStatus === CampaignCampaignStatus.CLOSED) {
    return (
      <Typography size="sm" weight={600} className={className}>
        Campaign ended
      </Typography>
    );
  }
  return (
    <Typography size="sm" weight={600} className={className}>
      Campaign coming soon
    </Typography>
  );
}

type CampaignViewCTAProps = ComponentProps<typeof Button> & {
  position?: "top" | "bottom";
};

function CampaignViewCTA({ className, position = "bottom", ...props }: CampaignViewCTAProps) {
  const info = useCurrentCampaignInfo();

  if (info.campaignStatus === CampaignCampaignStatus.SIGN_UP) {
    if (info.userRegisterStatus === CampaignUserRegisterStatus.REGISTERED) {
      return (
        <WorkspaceLink to={`${routes.CAMPAIGNS._detail(info.id)}`}>
          <Button size="lg" className={cn(`${info.meta?.styles.cta}`, className)} {...props}>
            View detail
          </Button>
        </WorkspaceLink>
      );
    }
    return <JoinCampaignButton id={info.id} position={position} className={className} {...props} />;
  }

  // Upcoming || Early closed || Closed
  return <JoinTheWaitlistButton className={className} position={position} {...props} />;
}

function JoinCampaignButton({
  id,
  position,
  className,
  ...props
}: ComponentProps<typeof Button> & { id: string; position: "top" | "bottom" }) {
  const { confirm } = useConfirm();
  const navigate = useWorkspaceNavigate();
  const workspaceId = useWorkspaceId();
  const info = useCurrentCampaignInfo();
  const {
    data: {
      data: { items: channels },
    },
  } = useGetChannelsApiV1Suspense(workspaceId);

  return (
    <Button
      size="lg"
      className={cn(`${info.meta?.styles.cta}`, className)}
      onClick={async () => {
        trackNewEvent(NewTrackingEvent.ClickJoinCampaignButton, {
          button_location: position,
          campaign_id: id,
        });

        if (channels.length === 0) {
          const confirmed = await confirm({
            title: "Add channel to join the campaign",
            titleClassName: "text-md",
            description:
              "You need to add your YouTube channel before you can join this campaign. Please add a channel now to continue",
            confirmText: "Add channel",
            cancelText: "Close",
          });

          if (confirmed) {
            navigate(routes.CHANNELS.root);
          }
        } else {
          navigate(routes.CAMPAIGNS._form(id));
        }
      }}
      {...props}
    >
      Join campaign
    </Button>
  );
}

type JoinTheWaitlistButtonProps = ComponentProps<typeof Button> & {
  position?: "top" | "bottom";
};

function JoinTheWaitlistButton({ className, position = "bottom", ...props }: JoinTheWaitlistButtonProps) {
  const workspaceId = useWorkspaceId();
  const info = useCurrentCampaignInfo();
  const queryClient = useQueryClient();
  const { info: showInfoMessage } = useToastMessage();
  const { mutate: joinCampaignWaitlist, isPending } = useJoinCampaignWaitlistApiV1({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getGetCampaignApiV1QueryKey(workspaceId, info.id) });
        showInfoMessage("You're on the waitlist");
      },
    },
  });

  if (info.waitlistRegisteredAt != null) {
    return (
      <Button
        size="lg"
        variant="outlined"
        className={cn(`border-2 ${info.meta?.styles.border} !text-black`, className)}
        leftAddon={<i className="i-bp-check inline-block size-4" />}
        disabled={true}
        {...props}
      >
        You're on the waitlist
      </Button>
    );
  }

  return (
    <Button
      size="lg"
      className={cn(`${info.meta?.styles.cta}`, className)}
      leftAddon={<i className="i-bp-notification inline-block size-4" />}
      loading={isPending}
      onClick={() => {
        trackNewEvent(NewTrackingEvent.ClickNewCampaignNotification, {
          button_location: position,
          campaign_id: info.id,
        });
        trackPixelReservedEvent("AddToWishlist");
        joinCampaignWaitlist({ workspaceId, campaignId: info.id });
      }}
      {...props}
    >
      Join waitlist
    </Button>
  );
}
